import React from "react"
import PropTypes from "prop-types"
import { Button, Flex } from "antd"
import { useTranslation } from "react-i18next"
import { Text } from "ui/Typography"
import { Icon } from "ui/Icon"
import IconWithBg from "ui/IconWithBg"
import BlackoutReasonChip from "ui/Blackout/BlackoutReasonChip"
import BookingsActionAlert from "ui/Blackout/BookingsActionAlert"
import { buildBlackoutRuns, getDayChipProps, hasBlackoutAction } from "pagesV2/pro/Calendar/blackoutUtils"
import EventBlock from "../EventBlock"
import { getJournalIcon } from "../../../utils"
import { getEventBorderColor } from "pagesV2/pro/Calendar/utils"
import styles from "../index.module.css"

const NonAgendaWeekView = ({ calendarDays, normalizedEventsByDay, timeOffByDay, isWeekEmpty, journalNoteDates, isCompact, active, canViewJournal, canCreateJournal, canEditJournal, getVisibleCount, showMore, showLess, MAX_VISIBLE, setIsBookingModalOpen, setSelectedBookingId, onJournalOpen, getDayBlackout, getBookingConflict, onOpenDayPanel }) => {
  const { t } = useTranslation()

  const dayKeys = calendarDays.map((day) => day.format("YYYY-MM-DD"))
  const blackoutRuns = buildBlackoutRuns(dayKeys, getDayBlackout)
  const hasAnyBlackout = blackoutRuns.some((run) => run.day)

  return (
    <div className={styles.nonAgendaWrapper}>
      <table className={styles.nonAgendaTable}>
        <thead>
          <tr>
            {calendarDays.map((day) => {
              const dayStr = day.format("YYYY-MM-DD")
              const hasJournalNote = journalNoteDates?.includes(dayStr)
              const hasJournalPermission = hasJournalNote ? canViewJournal || canEditJournal : canCreateJournal
              const dayLabel = isCompact ? day.format("dd")[0] : day.format("ddd")
              return (
                <th key={dayStr} className={styles.nonAgendaHeaderCell}>
                  <Flex gap={10}>
                    {hasBlackoutAction(getDayBlackout(dayStr)) && (
                      <span className={styles.weekHeaderAlert} aria-hidden>
                        <Icon name="AlertTriangle" width={20} height={20} color="var(--color-error)" />
                      </span>
                    )}
                    <Flex vertical align="center" justify="center" className="flex-1">
                      <Text fw="500">{dayLabel}</Text>
                      <Text fw="500">{day.format("D")}</Text>
                    </Flex>
                    {hasJournalPermission && hasJournalNote && (
                      <Flex justify="center" className="mt-xxs">
                        <IconWithBg disabled={!active} icon={getJournalIcon(journalNoteDates, dayStr)} onClick={active ? () => onJournalOpen(dayStr) : undefined} />
                      </Flex>
                    )}
                  </Flex>
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {/* One bar per run of days sharing the same blackouts, so a multi-day closure
              reads as a single span across its columns. */}
          {hasAnyBlackout && (
            <tr>
              {blackoutRuns.map((run) => {
                const chip = run.day && getDayChipProps(run.day)
                return (
                  <td key={run.dateKey} colSpan={run.span} className={styles.weekBlackoutBannerCell}>
                    {chip && (
                      <BlackoutReasonChip
                        label={chip.label || t(chip.labelKey, { defaultValue: chip.labelDefault })}
                        variant={chip.variant}
                        count={chip.count}
                        showCount
                        onClick={onOpenDayPanel ? () => onOpenDayPanel(run.dateKey) : undefined}
                      />
                    )}
                  </td>
                )
              })}
            </tr>
          )}
          {!isWeekEmpty && (
            <>
              <tr>
                {calendarDays.map((day) => {
                  const dayStr = day.format("YYYY-MM-DD")
                  const dayEvents = normalizedEventsByDay[dayStr] || []
                  const visibleCount = getVisibleCount(dayStr)
                  const visibleEvents = dayEvents.slice(0, visibleCount)
                  const allVisible = visibleCount >= dayEvents.length
                  const canShowLess = allVisible && dayEvents.length > MAX_VISIBLE
                  const timeOffForDay = timeOffByDay[dayStr]
                  const dayBlackout = getDayBlackout(dayStr)
                  return (
                    <td
                      key={dayStr}
                      className={`${styles.nonAgendaBodyCell} ${dayBlackout ? styles.weekBlackoutBodyCell : ""}`}
                    >
                      {dayBlackout && <BookingsActionAlert count={dayBlackout.bookings_requiring_action} isCompact className={styles.weekBlackoutAlert} />}
                      {timeOffForDay?.allDay && <div aria-hidden className={styles.nonAgendaAllDayOverlay} />}
                      {!timeOffForDay?.allDay && (timeOffForDay?.ranges || []).length > 0 && (
                        <>
                          <div aria-hidden className={styles.nonAgendaTimeOffOverlay} />
                          <div className={styles.nonAgendaTimeOffRangesList}>
                            {timeOffForDay.ranges.map((toff) => (
                              <div key={`${dayStr}-${toff.start.format("HH:mm")}`} className={styles.nonAgendaTimeOffRange}>
                                {toff.start.format("h:mm A")} – {toff.end.format("h:mm A")}
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                      {visibleEvents.map((ev) => (
                        <EventBlock key={ev.id ?? ev.title + ev.start} event={ev} eventColor={ev.color} eventBorderColor={getEventBorderColor()} isBlackoutConflict={Boolean(getBookingConflict(ev))} setIsBookingModalOpen={setIsBookingModalOpen} setSelectedBookingId={setSelectedBookingId} />
                      ))}
                      {!allVisible && (
                        <Button className={styles.viewMoreButton} type="link" size="small" onClick={() => showMore(dayStr)}>{t("calendar.view_more")}</Button>
                      )}
                      {canShowLess && (
                        <Button className={styles.viewLessButton} type="link" size="small" onClick={() => showLess(dayStr)}>{t("calendar.view_less", "View less")}</Button>
                      )}
                    </td>
                  )
                })}
              </tr>
              {Array.from({ length: 3 }, (_, i) => (
                <tr key={`dummy-${i}`}>
                  {calendarDays.map((day) => (
                    <td key={day.format("YYYY-MM-DD")} className={styles.nonAgendaDummyCell} />
                  ))}
                </tr>
              ))}
            </>
          )}
          {isWeekEmpty && (
            <>
              {Array.from({ length: 4 }, (_, i) => (
                <tr key={`dummy-${i}`}>
                  {calendarDays.map((day) => (
                    <td key={day.format("YYYY-MM-DD")} className={styles.nonAgendaDummyCell} />
                  ))}
                </tr>
              ))}
            </>
          )}
        </tbody>
      </table>
    </div>
  )
}

NonAgendaWeekView.propTypes = {
  calendarDays: PropTypes.array.isRequired,
  normalizedEventsByDay: PropTypes.object.isRequired,
  timeOffByDay: PropTypes.object.isRequired,
  isWeekEmpty: PropTypes.bool,
  journalNoteDates: PropTypes.array,
  isCompact: PropTypes.bool,
  active: PropTypes.bool,
  canViewJournal: PropTypes.bool,
  canCreateJournal: PropTypes.bool,
  canEditJournal: PropTypes.bool,
  getVisibleCount: PropTypes.func.isRequired,
  showMore: PropTypes.func.isRequired,
  showLess: PropTypes.func.isRequired,
  MAX_VISIBLE: PropTypes.number,
  setIsBookingModalOpen: PropTypes.func.isRequired,
  setSelectedBookingId: PropTypes.func.isRequired,
  onJournalOpen: PropTypes.func.isRequired,
  /** Looks a "YYYY-MM-DD" up in the blackout feed. */
  getDayBlackout: PropTypes.func.isRequired,
  /** Returns the blackout a booking clashes with, or null. */
  getBookingConflict: PropTypes.func.isRequired,
  onOpenDayPanel: PropTypes.func,
}

export default NonAgendaWeekView
